'use client'

// TouchControls — walking on a phone.
//
// With the CCTV rig gone, first person is the only way into the world, and a
// phone has no keyboard and no pointer lock. So: a thumb on the left half is
// an analog stick (push up to walk, sideways to turn), a thumb on the right
// half looks around. The stick is invisible until you touch, and it appears
// wherever your thumb lands rather than in a fixed corner, which is what
// makes it feel natural instead of like a gamepad bolted to the screen.
//
// Values go into the touchInput mailbox; FirstPersonController reads them each
// frame. The knob is moved by writing style directly, so a drag never
// re-renders React or the scene.

import { useEffect, useRef, useState } from 'react'
import { touchInput, resetTouchInput } from '../../lib/input/touchInput'
import { unlockAudio, loadFootsteps } from '../../lib/audio/footsteps'

const STICK_RADIUS = 46      // px from origin for full deflection
const DEAD_ZONE = 6          // px of slop before he starts moving

export function TouchControls() {
  const [touch, setTouch] = useState(false)
  const ringRef = useRef(null)
  const knobRef = useRef(null)
  // Live finger ids. Kept in a ref so the listeners never go stale.
  const fingers = useRef({ move: null, look: null })

  useEffect(() => {
    setTouch(!!window.matchMedia?.('(pointer: coarse)').matches)
  }, [])

  useEffect(() => {
    if (!touch) return

    const showStick = (x, y) => {
      const ring = ringRef.current
      if (!ring) return
      ring.style.left = `${x}px`
      ring.style.top = `${y}px`
      ring.style.opacity = '1'
      if (knobRef.current) knobRef.current.style.transform = 'translate(-50%, -50%)'
    }
    const hideStick = () => {
      if (ringRef.current) ringRef.current.style.opacity = '0'
    }

    // Only touches that land on the WORLD become walking. This listener is
    // on window, so without the check a finger scrolling an open drawer, or
    // pressing the menu, was also quietly steering the bird underneath it
    // (and a drawer scroll while walking is two gestures fighting one
    // finger). The world is the canvas; everything else is chrome.
    const onWorld = (t) => {
      const el = document.elementFromPoint(t.clientX, t.clientY)
      return !!el && (el.tagName === 'CANVAS' || !!el.closest?.('[data-walk-surface]'))
    }

    const onStart = (e) => {
      // First touch is also the gesture that lets audio play.
      unlockAudio()
      loadFootsteps()
      for (const t of e.changedTouches) {
        if (!onWorld(t)) continue
        const leftHalf = t.clientX < window.innerWidth * 0.5
        if (leftHalf && fingers.current.move === null) {
          fingers.current.move = { id: t.identifier, ox: t.clientX, oy: t.clientY }
          showStick(t.clientX, t.clientY)
        } else if (!leftHalf && fingers.current.look === null) {
          fingers.current.look = { id: t.identifier, x: t.clientX, y: t.clientY }
        }
      }
    }

    const onMove = (e) => {
      // A drag we own must not also scroll, bounce, or pull-to-refresh the
      // page: iOS treats a downward look-drag near the top as a refresh
      // gesture and cancels our touch mid-look. Only claimed fingers are
      // swallowed, so drawer scrolling and button taps stay native.
      const owned = [...e.changedTouches].some((t) =>
        (fingers.current.move && t.identifier === fingers.current.move.id) ||
        (fingers.current.look && t.identifier === fingers.current.look.id))
      if (owned && e.cancelable) e.preventDefault()
      for (const t of e.changedTouches) {
        const mv = fingers.current.move
        if (mv && t.identifier === mv.id) {
          let dx = t.clientX - mv.ox
          let dy = t.clientY - mv.oy
          const len = Math.hypot(dx, dy)
          if (len > STICK_RADIUS) {
            dx = (dx / len) * STICK_RADIUS
            dy = (dy / len) * STICK_RADIUS
          }
          if (knobRef.current) {
            knobRef.current.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`
          }
          const live = Math.hypot(dx, dy) > DEAD_ZONE
          touchInput.moveX = live ? dx / STICK_RADIUS : 0
          // Screen-down is positive dy, walking forward is pushing up.
          touchInput.moveY = live ? -dy / STICK_RADIUS : 0
        }
        const lk = fingers.current.look
        if (lk && t.identifier === lk.id) {
          touchInput.lookDX += t.clientX - lk.x
          touchInput.lookDY += t.clientY - lk.y
          lk.x = t.clientX
          lk.y = t.clientY
        }
      }
    }

    const onEnd = (e) => {
      for (const t of e.changedTouches) {
        if (fingers.current.move && t.identifier === fingers.current.move.id) {
          fingers.current.move = null
          touchInput.moveX = 0
          touchInput.moveY = 0
          hideStick()
        }
        if (fingers.current.look && t.identifier === fingers.current.look.id) {
          fingers.current.look = null
        }
      }
    }

    // passive:false so a drag can't scroll or rubber-band the page under us.
    const opts = { passive: false }
    const swallow = (e) => { e.preventDefault(); }
    window.addEventListener('touchstart', onStart, opts)
    window.addEventListener('touchmove', onMove, opts)
    window.addEventListener('touchend', onEnd, opts)
    window.addEventListener('touchcancel', onEnd, opts)
    document.addEventListener('gesturestart', swallow, opts)

    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
      document.removeEventListener('gesturestart', swallow)
      fingers.current = { move: null, look: null }
      resetTouchInput()
    }
  }, [touch])

  if (!touch) return null

  return (
    <div
      ref={ringRef}
      className="fixed z-30 pointer-events-none"
      style={{
        width: STICK_RADIUS * 2,
        height: STICK_RADIUS * 2,
        marginLeft: -STICK_RADIUS,
        marginTop: -STICK_RADIUS,
        borderRadius: '50%',
        border: '1.5px solid rgba(255,255,255,0.28)',
        background: 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(2px)',
        opacity: 0,
        transition: 'opacity 160ms ease',
        left: 0,
        top: 0,
      }}
    >
      <div
        ref={knobRef}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 34,
          height: 34,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.5)',
          transform: 'translate(-50%, -50%)',
        }}
      />
    </div>
  )
}

export default TouchControls
